import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { EntityRef } from '../../components/EntityRef'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill } from '../../components/StatusPill'
import { WidgetErrorBoundary } from '../insights/widgets/WidgetErrorBoundary'
import { createTask, listTasks, transitionTaskStatus, updateTask } from '../../api/tasks'
import { ApiError } from '../../api/problem'
import { rsqlAnd, rsqlEquals } from '../../lib/rsql'
import {
  TASK_STATUSES,
  TASK_STATUS_LABEL,
  TASK_PRIORITY_LABEL,
  type TaskStatus,
  type TaskView,
} from '../../types/task'
import { TaskBoard } from './TaskBoard'
import { TaskFormModal, type TaskFormValues } from './TaskFormModal'
import { useCaseContexts } from './useCaseContexts'

const PAGE_LIMIT = 100

type ViewMode = 'list' | 'board'
type StatusFilter = 'all' | TaskStatus

const STATUS_VARIANT: Record<TaskStatus, 'neutral' | 'info' | 'warning' | 'success'> = {
  todo: 'neutral',
  in_progress: 'info',
  blocked: 'warning',
  done: 'success',
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString()
}

function parseStatus(raw: string | null): StatusFilter {
  return (TASK_STATUSES as readonly string[]).includes(raw ?? '') ? (raw as TaskStatus) : 'all'
}

/**
 * "My Work" (`/my-work`) — replaces the M9/M14 `ComingSoonStub`. A cross-case work-queue: list AND
 * Kanban board views (toggle), filterable by status/case/assignee. Every case-linked row carries
 * Account · Legal entity · Case · Fiscal year context (CLAUDE.md §4), resolved client-side via
 * {@link useCaseContexts} — `TaskView` itself is not denormalized (disclosed gap, see
 * `module.task`'s backend README).
 */
export function MyWorkScreen() {
  const [searchParams, setSearchParams] = useSearchParams()
  const status = parseStatus(searchParams.get('status'))
  const [viewMode, setViewMode] = useState<ViewMode>('list')

  const [caseIdFilter, setCaseIdFilter] = useState('')
  const [assigneeFilter, setAssigneeFilter] = useState('')

  const [tasks, setTasks] = useState<TaskView[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modal, setModal] = useState<'create' | TaskView | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const filterString = useMemo(
    () =>
      rsqlAnd(
        status !== 'all' ? rsqlEquals('status', status) : null,
        caseIdFilter.trim() ? rsqlEquals('caseId', caseIdFilter.trim()) : null,
        assigneeFilter.trim() ? rsqlEquals('assigneeUserId', assigneeFilter.trim()) : null,
      ) || null,
    [status, caseIdFilter, assigneeFilter],
  )

  const latestRequestRef = useRef(0)

  const fetchPage = useCallback(
    async (afterCursor: string | null, replace: boolean) => {
      const requestId = ++latestRequestRef.current
      try {
        if (replace) {
          setLoading(true)
          setError(null)
        }
        const page = await listTasks({ filter: filterString, cursor: afterCursor, limit: PAGE_LIMIT })
        if (latestRequestRef.current !== requestId) return
        setTasks((prev) => (replace ? page.items : [...prev, ...page.items]))
        setCursor(page.nextCursor)
      } catch (err) {
        if (latestRequestRef.current !== requestId) return
        setError(
          err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load tasks. Check your connection and try again.',
        )
      } finally {
        if (latestRequestRef.current === requestId) setLoading(false)
      }
    },
    [filterString],
  )

  useEffect(() => {
    void fetchPage(null, true)
  }, [fetchPage])

  const caseContexts = useCaseContexts(tasks.map((t) => t.caseId))

  function onStatusFilterChange(next: StatusFilter) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (next === 'all') params.delete('status')
        else params.set('status', next)
        return params
      },
      { replace: true },
    )
  }

  async function handleSave(values: TaskFormValues) {
    setActionError(null)
    try {
      if (modal === 'create') {
        const created = await createTask(values)
        setTasks((prev) => [created, ...prev])
      } else if (modal) {
        const updated = await updateTask(modal.id, values)
        setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))
      }
      setModal(null)
    } catch (err) {
      setActionError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not save this task.')
      throw err
    }
  }

  async function handleTransition(taskId: string, toStatus: TaskStatus) {
    setActionError(null)
    try {
      const updated = await transitionTaskStatus(taskId, toStatus)
      setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))
      setModal((current) => (current && current !== 'create' && current.id === updated.id ? updated : current))
    } catch (err) {
      setActionError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not change status.')
    }
  }

  return (
    <div style={{ padding: 'var(--iw-space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--iw-accent)' }}>
          <Icon name="activity" size={22} />
        </span>
        <h1 style={{ margin: 0, fontSize: 'var(--iw-size-xl)' }}>My Work</h1>
        <span style={{ flex: 1 }} />
        <div role="group" aria-label="View">
          <button
            type="button"
            className="iw-chip"
            aria-pressed={viewMode === 'list'}
            onClick={() => setViewMode('list')}
          >
            <Icon name="rows" size={14} /> List
          </button>{' '}
          <button
            type="button"
            className="iw-chip"
            aria-pressed={viewMode === 'board'}
            onClick={() => setViewMode('board')}
          >
            <Icon name="columns" size={14} /> Board
          </button>
        </div>
        <button type="button" className="iw-btn iw-btn--primary" onClick={() => setModal('create')}>
          <Icon name="plus" size={14} /> New task
        </button>
      </div>

      <div style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div role="group" aria-label="Status filter" style={{ display: 'flex', gap: 'var(--iw-space-2)' }}>
          {(['all', ...TASK_STATUSES] as StatusFilter[]).map((s) => (
            <button
              key={s}
              type="button"
              className="iw-chip"
              aria-pressed={status === s}
              onClick={() => onStatusFilterChange(s)}
            >
              {s === 'all' ? 'All' : TASK_STATUS_LABEL[s]}
            </button>
          ))}
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="my-work-case-filter">
            Case ID
          </label>
          <input
            id="my-work-case-filter"
            className="iw-input"
            value={caseIdFilter}
            onChange={(e) => setCaseIdFilter(e.target.value)}
            placeholder="Filter by case id"
          />
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="my-work-assignee-filter">
            Assignee ID
          </label>
          <input
            id="my-work-assignee-filter"
            className="iw-input"
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            placeholder="Filter by assignee id"
          />
        </div>
      </div>

      {actionError ? <InlineAlert severity="error">{actionError}</InlineAlert> : null}

      <WidgetErrorBoundary title="My Work" icon="activity">
        {error ? (
          <InlineAlert
            severity="error"
            action={
              <button type="button" className="iw-btn iw-btn--sm" onClick={() => void fetchPage(null, true)}>
                Try again
              </button>
            }
          >
            {error}
          </InlineAlert>
        ) : loading ? (
          <p style={{ color: 'var(--iw-text-muted)' }}>Loading tasks…</p>
        ) : tasks.length === 0 ? (
          <div style={{ color: 'var(--iw-text-muted)', textAlign: 'center', padding: 'var(--iw-space-6)' }}>
            <Icon name="inbox" size={28} />
            <p>No tasks match this view yet.</p>
          </div>
        ) : viewMode === 'board' ? (
          <TaskBoard tasks={tasks} caseContexts={caseContexts} onCardClick={(t) => setModal(t)} />
        ) : (
          <table className="iw-table" aria-label="Tasks">
            <thead>
              <tr>
                <th scope="col">Title</th>
                <th scope="col">Account</th>
                <th scope="col">Legal entity</th>
                <th scope="col">Case</th>
                <th scope="col">Fiscal year</th>
                <th scope="col">Status</th>
                <th scope="col">Priority</th>
                <th scope="col">Due</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => {
                const ctx = task.caseId ? caseContexts.get(task.caseId) : undefined
                return (
                  <tr key={task.id}>
                    <td>{task.title}</td>
                    <td>{ctx?.accountName ?? (task.caseId ? '—' : 'Unlinked')}</td>
                    <td>{ctx?.legalEntityName ?? (task.caseId ? '—' : '—')}</td>
                    <td>
                      {task.caseId ? (
                        <Link to={`/cases/${task.caseId}`}>{ctx?.caseLabel ?? 'Open case'}</Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <EntityRef kind="fiscalYear" id={ctx?.fiscalYearId} name={ctx?.fiscalYearLabel} />
                    </td>
                    <td>
                      <StatusPill variant={STATUS_VARIANT[task.status]} label={TASK_STATUS_LABEL[task.status]} />
                    </td>
                    <td>{TASK_PRIORITY_LABEL[task.priority]}</td>
                    <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                      {formatDate(task.dueDate)}
                    </td>
                    <td>
                      <button type="button" className="iw-btn iw-btn--sm" onClick={() => setModal(task)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </WidgetErrorBoundary>

      {cursor && !loading ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button type="button" className="iw-btn" onClick={() => void fetchPage(cursor, false)}>
            Load more
          </button>
        </div>
      ) : null}

      {modal ? (
        <TaskFormModal
          task={modal === 'create' ? undefined : modal}
          allowCaseIdEntry={modal === 'create'}
          onClose={() => setModal(null)}
          onSave={handleSave}
          onTransitionStatus={
            modal === 'create' ? undefined : (toStatus) => handleTransition((modal as TaskView).id, toStatus)
          }
        />
      ) : null}
    </div>
  )
}
